import { apiGet, apiPost } from "@/services/http";
import { endpoints } from "./endpoints";
import type { ApiEnvelope, ApiErrorBody } from "@/types/api";
import { ApiError } from "@/services/http/errors";

export type DirectoryContactDto = {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  description?: string;
  group_id?: number | null;
  group_name?: string | null;
  created_by_id?: number | null;
  created_by_name?: string | null;
  created_by_email?: string | null;
  created?: string;
  updated?: string;
};

export type DirectoryContactGroupDto = {
  id: number;
  name: string;
  description?: string;
  contact_count?: number;
  access_member_ids?: number[];
  access_member_count?: number;
  created?: string;
  updated?: string;
};

export async function fetchDirectoryContactGroups() {
  const response = await apiGet<ApiEnvelope<DirectoryContactGroupDto[]>>(
    endpoints.directoryContactGroups,
  );

  if (!response?.success || !Array.isArray(response.data)) {
    throw new ApiError(0, response, response?.message || "Could not load contact groups.");
  }

  return response.data;
}

export async function fetchDirectoryContacts(params?: {
  groupId?: number | string;
  search?: string;
}) {
  const query = new URLSearchParams();
  if (params?.groupId != null && params.groupId !== "" && params.groupId !== "all") {
    query.set("group_id", String(params.groupId));
  }
  if (params?.search?.trim()) {
    query.set("search", params.search.trim());
  }
  const qs = query.toString();
  const url = `${endpoints.directoryContacts}${qs ? `?${qs}` : ""}`;

  const response = await apiGet<ApiEnvelope<DirectoryContactDto[]>>(url);

  if (!response?.success || !Array.isArray(response.data)) {
    throw new ApiError(0, response, response?.message || "Could not load contacts.");
  }

  return response.data;
}

export type CreateDirectoryContactPayload = {
  name: string;
  phone?: string;
  email?: string;
  description?: string;
  group_id?: number | null;
};

export type CreateDirectoryContactGroupPayload = {
  name: string;
  description?: string;
};

export async function createDirectoryContact(payload: CreateDirectoryContactPayload) {
  const response = await apiPost<
    ApiEnvelope<DirectoryContactDto> & ApiErrorBody,
    CreateDirectoryContactPayload
  >(endpoints.directoryContacts, payload);

  if (!response?.success || !response.data) {
    throw new ApiError(
      0,
      response,
      response?.error?.message || response?.message || "Could not create contact.",
    );
  }

  return response.data;
}

export async function createDirectoryContactGroup(
  payload: CreateDirectoryContactGroupPayload,
) {
  const response = await apiPost<
    ApiEnvelope<DirectoryContactGroupDto> & ApiErrorBody,
    CreateDirectoryContactGroupPayload
  >(endpoints.directoryContactGroups, payload);

  if (!response?.success || !response.data) {
    throw new ApiError(
      0,
      response,
      response?.error?.message || response?.message || "Could not create contact group.",
    );
  }

  return response.data;
}
